/* eslint-disable react/no-unescaped-entities, react/jsx-key */
import { motion } from "framer-motion";
import { MapPin, ExternalLink } from "lucide-react";
import { Logo } from "@/components/Logo";

const cities = [
  { name: "Marssac-sur-Tarn", desc: "Notre siège — accompagnement en face à face." },
  { name: "Albi", desc: "Capitale économique — commerces, services, professions libérales." },
  { name: "Gaillac", desc: "Bassin viticole et artisanal en pleine croissance." },
  { name: "Castres", desc: "PME industrielles, santé, services." },
  { name: "Graulhet", desc: "Maroquinerie, cuir, industrie." },
  { name: "Carmaux", desc: "Commerces de proximité et artisans." },
];

const photos = [
  {
    url: "https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
    label: "Viticulteur",
    alt: "Viticulteur du Tarn dans ses vignes — WebUP accompagne les domaines viticoles",
  },
  {
    url: "https://images.unsplash.com/photo-1526397751294-331021109fbd?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
    label: "Fleuriste",
    alt: "Fleuriste dans sa boutique — création de sites internet pour fleuristes par WebUP",
  },
  {
    url: "https://images.unsplash.com/photo-1568254183919-78a4f43a2877?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
    label: "Boulanger",
    alt: "Boulanger façonnant du pain — WebUP développe la visibilité en ligne des boulangeries",
  },
  {
    url: "https://images.unsplash.com/photo-1632823469850-2f77dd9c7f93?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
    label: "Garagiste",
    alt: "Garagiste au travail sur un véhicule — sites internet pour garages et carrossiers par WebUP",
  },
];

export const ZonesLocales = () => (
  <section id="zones" data-testid="zones-section" className="py-24 md:py-32 bg-paper relative overflow-hidden">
    <div className="absolute inset-0 dot-grid opacity-30 -z-0" aria-hidden />
    <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <Logo size="xl" showLabel={false} />
          <p className="font-mono-label text-cyan-600 flex items-center mt-8">
            <span className="rule bg-cyan" /> Zones d'intervention
          </p>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl text-ink tracking-tight leading-[1.05]">
            Une agence <em className="italic font-light">profondément ancrée</em> dans le Tarn.
          </h2>
          <p className="mt-6 text-ink/75 leading-relaxed">
            <strong className="text-ink">Marius, fondateur de WebUP</strong>,
            travaille en priorité avec les entreprises du 81. Connaître votre
            terrain, ça change tout.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-3">
            {photos.map((p) => (
              <motion.figure
                key={p.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="relative overflow-hidden aspect-square group"
              >
                <img src={p.url} alt={p.alt || p.label} title={p.label} width="400" height="400" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <figcaption className="absolute bottom-2 left-2 text-[10px] font-mono-label text-white bg-ink/80 px-2 py-1">
                  {p.label}
                </figcaption>
              </motion.figure>
            ))}
          </div>
          <p className="mt-3 text-[10px] text-ink/45 flex items-center gap-1.5">
            <ExternalLink size={10} /> Images illustratives — photothèque libre de droits.
          </p>
        </div>

        <div className="lg:col-span-7">
          <ul className="space-y-3">
            {cities.map((c, i) => (
              <motion.li
                key={c.name}
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-white border border-ink/10 p-6 lg:p-7 flex items-start gap-5 lift"
                data-testid={`city-${i}`}
              >
                <span className="h-12 w-12 rounded-full bg-gradient-to-br from-ink to-cyan-600 text-white inline-flex items-center justify-center shrink-0">
                  <MapPin size={18} />
                </span>
                <div className="flex-1">
                  <p className="font-display text-2xl text-ink">{c.name}</p>
                  <p className="mt-1 text-sm text-ink/65 leading-relaxed">{c.desc}</p>
                </div>
                <span className="hidden md:inline-flex font-mono-label text-cyan-600 mt-2">→</span>
              </motion.li>
            ))}
          </ul>

          <div className="mt-6 p-6 bg-gradient-to-br from-ink to-ink-600 text-white relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-44 h-44 bg-cyan/30 rounded-full blur-3xl" aria-hidden />
            <p className="relative font-display text-xl">
              Vous êtes ailleurs en Occitanie ?
            </p>
            <p className="relative mt-2 text-sm text-white/75">
              Nous intervenons également partout en région — discutons-en par
              visio.
            </p>
            <a href="#contact" className="relative mt-4 inline-flex items-center gap-2 text-sm text-cyan-300 link-under">
              Réserver un appel découverte →
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);
